import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { useFlash } from 'contexts/flash';
import {
  loadMaintenanceIssueById,
  updateMaintenanceIssue,
  MaintenanceIssue,
  MaintenanceIssuePhoto
} from 'services/report';
import { buildIssueAssignmentPutFields } from 'utils/issueAssignmentPutFields';
import { getIssuesConfig } from 'services/option';
import { DEFAULT_ISSUES_UI_CONFIG, IssuesUiConfig } from 'utils/issueDueDateColor';
import { DEFAULT_CRITICALITY_LEVELS } from 'utils/issueCriticality';
import { getApiErrorMessage } from 'utils/apiErrorMessage';
import type { IssueAssignmentFormValues } from '../components/IssueAssignmentFields';

type PhotoRow = {
  id: number;
  url: string;
  storage: { fileName: string; type: string };
};

export const useMaintenanceIssueDialog = ({
  isOpen,
  issueId,
  onSaved
}: {
  isOpen: boolean;
  issueId?: number;
  onSaved?: () => void;
}) => {
  const { setFlash } = useFlash();
  const [loading, setLoading] = useState(false);
  const [issue, setIssue] = useState<MaintenanceIssue | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);
  const [newBeforePhotos, setNewBeforePhotos] = useState<File[]>([]);
  const [newAfterPhotos, setNewAfterPhotos] = useState<File[]>([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState<number[]>([]);
  const [notes, setNotes] = useState('');

  const assignmentForm = useForm<IssueAssignmentFormValues>({
    defaultValues: {
      assignedToUserId: '',
      dueDate: null,
      status: 'ACTIVE',
      criticalityKey: ''
    }
  });

  const criticalityLevels = issuesConfig.criticalityLevels?.length
    ? issuesConfig.criticalityLevels
    : DEFAULT_CRITICALITY_LEVELS;

  const loadIssue = useCallback(async () => {
    if (!issueId) return;
    setLoading(true);
    try {
      const data = await loadMaintenanceIssueById(issueId);
      const loaded = data.issue;
      setIssue(loaded);
      setNewBeforePhotos([]);
      setNewAfterPhotos([]);
      setDeletedPhotoIds([]);
      setNotes(loaded.notes || '');

      assignmentForm.reset({
        assignedToUserId: loaded.assignedTo?.id ? String(loaded.assignedTo.id) : '',
        dueDate: loaded.dueDate ? dayjs(loaded.dueDate) : null,
        status: loaded.status || 'ACTIVE',
        criticalityKey: loaded.criticalityKey || ''
      });
    } catch (err) {
      setFlash({
        type: 'error',
        message: getApiErrorMessage(err, 'Failed to load maintenance issue.')
      });
    } finally {
      setLoading(false);
    }
  }, [issueId, assignmentForm, setFlash]);

  useEffect(() => {
    getIssuesConfig()
      .then((res) => setIssuesConfig({ ...DEFAULT_ISSUES_UI_CONFIG, ...(res?.config || {}) }))
      .catch(() => setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG));
  }, []);

  useEffect(() => {
    if (!isOpen || !issueId) return;
    setIsEditing(false);
    loadIssue();
  }, [isOpen, issueId, loadIssue]);

  const mapPhotosForManagement = useCallback(
    (photos: MaintenanceIssuePhoto[]): PhotoRow[] =>
      photos
        .filter((p) => !deletedPhotoIds.includes(Number(p.id)))
        .map((p) => ({
          id: Number(p.id),
          url: p.url,
          storage: { fileName: `Photo ${p.id}`, type: 'image' }
        })),
    [deletedPhotoIds]
  );

  const existingBeforePhotos = useMemo(
    () =>
      mapPhotosForManagement((issue?.photos || []).filter((p) => p.photoType !== 'ISSUE_AFTER')),
    [issue?.photos, mapPhotosForManagement]
  );

  const existingAfterPhotos = useMemo(
    () =>
      mapPhotosForManagement((issue?.photos || []).filter((p) => p.photoType === 'ISSUE_AFTER')),
    [issue?.photos, mapPhotosForManagement]
  );

  const handleSave = useCallback(async () => {
    if (!issueId) return;
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    try {
      const assignmentValues = assignmentForm.getValues();
      const assignmentPutFields = buildIssueAssignmentPutFields(assignmentValues);
      const allNewFiles = [...newBeforePhotos, ...newAfterPhotos];
      const photoTypes = [
        ...newBeforePhotos.map(() => 'ISSUE_BEFORE'),
        ...newAfterPhotos.map(() => 'ISSUE_AFTER')
      ];

      await updateMaintenanceIssue(issueId, {
        ...assignmentPutFields,
        notes,
        deletePhotoIds: deletedPhotoIds.length ? deletedPhotoIds : undefined,
        photoTypes: allNewFiles.length ? photoTypes : undefined,
        files: allNewFiles.length ? allNewFiles : undefined
      });
      setFlash({ type: 'success', message: 'Maintenance issue updated successfully.' });
      setIsEditing(false);
      setNewBeforePhotos([]);
      setNewAfterPhotos([]);
      setDeletedPhotoIds([]);
      onSaved?.();
      await loadIssue();
    } catch (err) {
      setFlash({
        type: 'error',
        message: getApiErrorMessage(err, 'Failed to update maintenance issue.')
      });
    }
  }, [
    issueId,
    isEditing,
    assignmentForm,
    notes,
    deletedPhotoIds,
    newBeforePhotos,
    newAfterPhotos,
    setFlash,
    onSaved,
    loadIssue
  ]);

  return {
    loading,
    issue,
    isEditing,
    setIsEditing,
    issuesConfig,
    criticalityLevels,
    assignmentForm,
    existingBeforePhotos,
    existingAfterPhotos,
    newBeforePhotos,
    setNewBeforePhotos,
    newAfterPhotos,
    setNewAfterPhotos,
    deletedPhotoIds,
    setDeletedPhotoIds,
    notes,
    setNotes,
    handleSave,
    setIssue,
    reload: loadIssue
  };
};
